
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, input, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  BilledType,
  BillingTaskHours,
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_RANGE_OPTION,
  DateRangeOption,
  computeDateRange,
  formatBillingHours,
  getBilledTypeLabel,
} from '../../../models/billing-history.models';
import { AuthService } from '../../../services/auth.service';
import { Breadcrumb } from '../../../services/breadcrumb.service';
import { MatterService } from '../../../services/matter.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { SnackbarService } from '../../../services/snackbar.service';
import {
  PageKey,
  UserCookieService,
} from '../../../services/user-cookie.service';
import { BillingTotalTileComponent } from '../billing-total-tile/billing-total-tile.component';
import { TaskBreakdownComponent } from '../task-breakdown/task-breakdown.component';
import { TaskBreakdownExpansion } from '../task-breakdown/task-breakdown-expansion';
import { describeApiError } from '../../../utils/api-error.utils';

export interface BillingResourceData {
  employeeId: string;
  firstName: string;
  lastName: string;
  title: string;
  officeLocation: string;
  totalHours: number;
  totalBilledHours?: number;
  totalNonbilledHours?: number;
  totalProbonoHours?: number;
  totalBusinessDevelopmentHours?: number;
  totalAbsenceHours?: number;
  totalFirmHours?: number;
  billedType?: BilledType;
  practiceAreaCodes?: string[];
  /**
   * Hours this resource billed on this matter, broken down by task code. Null/undefined means
   * the feed supplied no task data — not that no task time was billed. Behind `task-breakdowns`.
   */
  taskSummary?: BillingTaskHours[] | null;
  isSkeleton?: boolean;
}

@Component({
  selector: 'app-matter-billing-history',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatSortModule,
    MatDatepickerModule,
    MatNativeDateModule,
    RouterModule,
    BillingTotalTileComponent,
    TaskBreakdownComponent,
  ],
  templateUrl: './matter-billing-history.component.html',
  styleUrls: ['./matter-billing-history.component.scss'],
})
export class MatterBillingHistoryComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private matterService = inject(MatterService);
  private snackbarService = inject(SnackbarService);
  private featureFlagService = inject(FeatureFlagService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private userCookieService = inject(UserCookieService);
  private router = inject(Router);

  readonly clientMatterId = input.required<string>();
  readonly breadcrumbContext = input<Breadcrumb[]>([]);
  readonly paginator = viewChild.required(MatPaginator);
  readonly sort = viewChild.required(MatSort);

  isLoading = false;
  error: string | null = null;
  billingData: BillingResourceData[] = [];
  dataSource!: MatTableDataSource<BillingResourceData>;
  selectedDateRange: DateRangeOption = DEFAULT_DATE_RANGE_OPTION; // Will be loaded from settings
  dateRangeOptions = DATE_RANGE_OPTIONS;
  private readonly SETTINGS_PAGE_KEY = PageKey.MatterDetails;
  pageSize = 5; // Default page size
  isDropdownOpen = false;
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;
  showStartDatePicker = false;
  showEndDatePicker = false;
  /** The 'expand' column is added only while the task-breakdowns flag is on — see ngOnInit. */
  displayedColumns: string[] = ['resource', 'billedType', 'totalHours'];
  /**
   * Gates the task-breakdown expander on the `task-breakdowns` flag. Starts false and is only
   * raised once the flag is confirmed enabled, so a slow or failed lookup hides the expander
   * rather than offering one that opens onto nothing.
   */
  taskBreakdownsEnabled = false;

  /** Which rows are open, plus the count label beside each chevron. */
  readonly expansion = new TaskBreakdownExpansion<BillingResourceData>();

  /**
   * Whether a row can be expanded at all. A skeleton row never can; a real row can even when
   * the feed supplied no task data, because "no task detail available" is itself worth
   * surfacing rather than leaving the user to wonder why some rows expand and others do not.
   */
  canExpand(row: BillingResourceData): boolean {
    return this.taskBreakdownsEnabled && !this.isSkeletonRow(row);
  }


  // Totals from the response
  totalBilledHours: number = 0;
  totalUnbilledHours: number = 0;
  totalProBonoHours: number = 0;
  totalBusinessDevelopmentHours: number = 0;
  totalAbsenceHours: number = 0;
  totalFirmHours: number = 0;
  grandTotalHours: number = 0;

  // Grand total formatted for the Total tile, matching the resource billing
  // card (one decimal, en dash when zero). Recomputed when totals change.
  grandTotalDisplay = '–';

  private destroy$ = new Subject<void>();
  private loadingSubscription?: Subscription;
  private requestCounter = 0;
  private clickListener?: () => void;

  ngOnInit(): void {
    this.featureFlagService
      .isFeatureFlagEnabled('task-breakdowns')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.taskBreakdownsEnabled = enabled;

        // Add/remove the column rather than just hiding its contents. The table is
        // `table-layout: fixed` with the name column at `width: auto`, which is not a
        // specified width — so an empty expand column would still claim half the remaining
        // space and halve the name column, flag off or not.
        const hasColumn = this.displayedColumns.includes('expand');
        if (enabled && !hasColumn) {
          this.displayedColumns = [...this.displayedColumns, 'expand'];
        } else if (!enabled && hasColumn) {
          this.displayedColumns = this.displayedColumns.filter((c) => c !== 'expand');
        }

        // A disabled flag must also close anything already open, not just hide the toggle.
        if (!enabled) {
          this.expansion.clear();
        }
      });

    this.dataSource = new MatTableDataSource<BillingResourceData>();
    if (!this.clientMatterId()) {
      this.error = 'Client Matter ID is required';
      return;
    }
    // Load saved timespan preference from settings
    this.selectedDateRange = this.userCookieService.getBillingHistoryTimespan(
      this.SETTINGS_PAGE_KEY,
    );

    // Load saved custom dates if custom range is selected
    if (this.isCustomRange()) {
      const savedDates = this.userCookieService.getCustomDateRange(
        this.SETTINGS_PAGE_KEY,
      );
      if (savedDates) {
        this.customStartDate = savedDates.startDate;
        this.customEndDate = savedDates.endDate;
      }
    }

    // Initialize custom dates with the loaded range
    this.initializeCustomDates();
    this.loadBillingHistory();
  }

  ngAfterViewInit(): void {
    // Connect paginator and sort after view initialization
    this.dataSource.paginator = this.paginator();
    this.dataSource.sort = this.sort();

    // Configure custom sort accessor for resource column to sort by lastName
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'resource') {
        return item.lastName?.toLowerCase() || '';
      }
      return (item as any)[property];
    };
  }

  ngOnDestroy(): void {
    this.cancelPendingRequest();
    this.removeClickListener();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPageChange(event: any): void {
    // Store the user's page size preference
    this.pageSize = event.pageSize;

    // If currently loading, update skeleton data to match new page size
    if (this.isLoading) {
      this.showSkeletonData();
    }
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen) {
      this.setupClickListener();
    } else {
      this.removeClickListener();
    }
  }

  selectDateRange(option: DateRangeOption): void {
    this.selectedDateRange = option;
    this.isDropdownOpen = false;
    this.removeClickListener();
    this.error = null;

    // Save the selected timespan to settings
    this.userCookieService.setBillingHistoryTimespan(
      this.SETTINGS_PAGE_KEY,
      option,
    );

    // Update custom dates to match the selected range
    this.initializeCustomDates();

    this.loadBillingHistory();
  }

  isCustomRange(): boolean {
    return this.selectedDateRange.kind === 'custom';
  }

  /**
   * Initialize custom dates based on the selected date range option
   */
  private initializeCustomDates(): void {
    const today = new Date();

    if (this.isCustomRange()) {
      // For custom range, only set defaults if not already loaded from cookie
      if (!this.customStartDate) {
        this.customStartDate = new Date(today);
        this.customStartDate.setDate(this.customStartDate.getDate() - 30);
      }
      if (!this.customEndDate) {
        this.customEndDate = new Date(today);
      }
    } else {
      // For preset ranges, seed the custom dates from the computed window so the
      // displayed start/end text stays in sync with the selection.
      const { startDate, endDate } = computeDateRange(this.selectedDateRange);
      this.customStartDate = this.parseLocalDate(startDate);
      this.customEndDate = this.parseLocalDate(endDate);
    }
  }

  /** Parse a local `yyyy-MM-dd` string into a Date at local midnight. */
  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  onStartDateChange(event: any): void {
    const date = event?.value || event;
    if (date) {
      this.customStartDate = date;
      this.showStartDatePicker = false;

      // Automatically switch to Custom range if not already selected
      if (!this.isCustomRange()) {
        this.selectedDateRange =
          this.dateRangeOptions.find((opt) => opt.kind === 'custom') ||
          this.selectedDateRange;
        // Save the custom range selection
        this.userCookieService.setBillingHistoryTimespan(
          this.SETTINGS_PAGE_KEY,
          this.selectedDateRange,
        );
      }

      // Save custom dates to cookie
      if (this.customStartDate && this.customEndDate) {
        this.userCookieService.setCustomDateRange(
          this.SETTINGS_PAGE_KEY,
          this.customStartDate,
          this.customEndDate,
        );
      }

      this.loadBillingHistory();
    }
  }

  onEndDateChange(event: any): void {
    const date = event?.value || event;
    if (date) {
      this.customEndDate = date;
      this.showEndDatePicker = false;

      // Automatically switch to Custom range if not already selected
      if (!this.isCustomRange()) {
        this.selectedDateRange =
          this.dateRangeOptions.find((opt) => opt.kind === 'custom') ||
          this.selectedDateRange;
        // Save the custom range selection
        this.userCookieService.setBillingHistoryTimespan(
          this.SETTINGS_PAGE_KEY,
          this.selectedDateRange,
        );
      }

      // Save custom dates to cookie
      if (this.customStartDate && this.customEndDate) {
        this.userCookieService.setCustomDateRange(
          this.SETTINGS_PAGE_KEY,
          this.customStartDate,
          this.customEndDate,
        );
      }

      this.loadBillingHistory();
    }
  }

  private setupClickListener(): void {
    // Close dropdown when clicking outside
    this.clickListener = () => {
      this.isDropdownOpen = false;
      this.removeClickListener();
      this.cdr.detectChanges();
    };

    // Add small delay to prevent immediate closing
    setTimeout(() => {
      document.addEventListener('click', this.clickListener!);
    }, 0);
  }

  private removeClickListener(): void {
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
      this.clickListener = undefined;
    }
  }

  private cancelPendingRequest(): void {
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
      this.loadingSubscription = undefined;
    }
  }

  private loadBillingHistory(): void {
    const clientMatterId = this.clientMatterId();
    if (!clientMatterId) {
      this.error = 'Client Matter ID is required';
      return;
    }

    // Cancel any pending request
    this.cancelPendingRequest();

    this.isLoading = true;
    this.error = null;
    // Drop expansions here rather than in each result branch, so the error and empty paths
    // release their rows too. The Set holds row objects, and this fetch is about to replace
    // every one of them — without this it would keep the old ones alive for good.
    this.expansion.clear();

    // Show skeleton data while loading
    this.showSkeletonData();

    // Increment request counter to track the latest request
    const currentRequestId = ++this.requestCounter;

    const { startDate, endDate } = this.calculateDateRange();

    this.loadingSubscription = this.matterService
      .getBillingHistory(clientMatterId, startDate, endDate)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          // Only update loading state if this is the latest request
          if (currentRequestId === this.requestCounter) {
            this.isLoading = false;
          }
        }),
      )
      .subscribe({
        next: (response: any) => {
          // Ignore stale responses
          if (currentRequestId !== this.requestCounter) {
            return;
          }

          if (response) {
            const resourceData = response.resources || [];

            // Store the totals from the response
            this.totalBilledHours = response.totalBilledHours ?? 0;
            this.totalUnbilledHours = response.totalNonbilledHours ?? 0;
            this.totalProBonoHours = response.totalProbonoHours ?? 0;
            this.totalBusinessDevelopmentHours =
              response.totalBusinessDevelopmentHours ?? 0;
            this.totalAbsenceHours = response.totalAbsenceHours ?? 0;
            this.totalFirmHours = response.totalFirmHours ?? 0;
            this.grandTotalHours = response.totalHours ?? 0;
            this.updateGrandTotalDisplay();

            // Flatten entries into individual rows
            this.billingData = [];
            resourceData.forEach((resource: any) => {
              if (resource.entries && resource.entries.length > 0) {
                resource.entries.forEach((entry: any) => {
                  this.billingData.push({
                    employeeId: resource.employeeId,
                    firstName: resource.firstName,
                    lastName: resource.lastName,
                    title: resource.title,
                    officeLocation: resource.officeLocation,
                    totalHours: entry.totalHours,
                    billedType: entry.billedType,
                    practiceAreaCodes: resource.practiceAreaCodes,
                    // This row IS one entry, so it carries that entry's own breakdown — the
                    // resource-level rollup would describe every bill type at once and overstate
                    // the row's own hours.
                    taskSummary: entry.taskSummary,
                  });
                });
              }
            });
            // Set data - the paginator will automatically detect the length
            // Stamp panel ids before the rows are rendered, so the template's
            // aria-controls binding is a lookup rather than an assignment.
            this.expansion.prepare(this.billingData);
            this.dataSource.data = this.billingData;

            // Force change detection to show table
            this.cdr.detectChanges();

            // Use setTimeout to ensure paginator and sort connect after view updates
            setTimeout(() => {
              // Ensure paginator and sort are connected
              const paginator = this.paginator();
              this.dataSource.paginator = paginator;
              paginator.firstPage();
              this.dataSource.sort = this.sort();
            }, 0);
          } else {
            // Clear data when no results
            this.billingData = [];
            this.dataSource.data = [];
            this.totalBilledHours = 0;
            this.totalUnbilledHours = 0;
            this.totalProBonoHours = 0;
            this.totalBusinessDevelopmentHours = 0;
            this.totalAbsenceHours = 0;
            this.totalFirmHours = 0;
            this.grandTotalHours = 0;
            this.updateGrandTotalDisplay();

            // Ensure paginator is reset for empty data
            this.paginator().firstPage();
          }
        },
        error: (error) => {
          // Ignore errors from stale requests
          if (currentRequestId !== this.requestCounter) {
            return;
          }

          this.error = describeApiError(error, 'Failed to load billing history');
          this.billingData = [];
          this.dataSource.data = [];
          this.totalBilledHours = 0;
          this.totalUnbilledHours = 0;
          this.totalProBonoHours = 0;
          this.totalBusinessDevelopmentHours = 0;
          this.totalAbsenceHours = 0;
          this.totalFirmHours = 0;
          this.grandTotalHours = 0;
          this.updateGrandTotalDisplay();

          // Reset paginator on error
          this.paginator().firstPage();

          this.snackbarService.showError('Failed to load billing history');
        },
      });
  }

  private calculateDateRange(): { startDate: string; endDate: string } {
    return computeDateRange(this.selectedDateRange, {
      startDate: this.customStartDate,
      endDate: this.customEndDate,
    });
  }

  retry(): void {
    this.loadBillingHistory();
  }

  /**
   * Format the grand total for the Total tile, matching the resource billing
   * card (one decimal, or an en dash when it rounds to zero).
   */
  private updateGrandTotalDisplay(): void {
    this.grandTotalDisplay = formatBillingHours(this.grandTotalHours);
  }

  /**
   * Get display label for billed type enum
   */
  getBilledTypeLabel(billedType: BilledType | null | undefined): string {
    return getBilledTypeLabel(billedType);
  }

  /**
   * Format hours for the table cells: an en dash when the value rounds to zero,
   * otherwise the value with trailing zeros stripped (matches the resource card).
   */
  formatHours(value: number): string {
    return formatBillingHours(value, { stripTrailingZeros: true });
  }

  /**
   * Get formatted date range text based on selected option
   */
  getDateRangeText(): string {
    const { startDate, endDate } = this.calculateDateRange();
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(startDateObj)} — ${formatDate(endDateObj)}`;
  }

  /**
   * Get formatted date range text based on selected option
   */
  getDateRangeStartText(): string {
    const { startDate } = this.calculateDateRange();
    // Create date from the ISO string components to avoid timezone shift
    const [year, month, day] = startDate.split('-').map(Number);
    const startDateObj = new Date(year, month - 1, day);

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(startDateObj)}`;
  }

  /**
   * Get formatted date range text based on selected option
   */
  getDateRangeEndText(): string {
    const { endDate } = this.calculateDateRange();
    // Create date from the ISO string components to avoid timezone shift
    const [year, month, day] = endDate.split('-').map(Number);
    const endDateObj = new Date(year, month - 1, day);

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(endDateObj)}`;
  }

  /**
   * Format a single date for the date picker display
   */
  formatSingleDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Show skeleton data while loading
   */
  private showSkeletonData(): void {
    this.dataSource.data = this.createSkeletonData(this.pageSize);
  }

  /**
   * Create skeleton data for the table
   */
  private createSkeletonData(count: number): BillingResourceData[] {
    return Array(count)
      .fill(null)
      .map((_, index) => ({
        employeeId: `SKELETON-${index}`,
        firstName: '',
        lastName: '',
        title: '',
        officeLocation: '',
        totalHours: 0,
        billedType: BilledType.Billable,
        totalBilledHours: 0,
        totalNonbilledHours: 0,
        totalProbonoHours: 0,
        totalBusinessDevelopmentHours: 0,
        totalAbsenceHours: 0,
        totalFirmHours: 0,
        // Add a flag to identify skeleton rows
        isSkeleton: true,
      }));
  }

  /**
   * Check if a row is a skeleton row
   */
  isSkeletonRow(element: any): boolean {
    return (
      element?.isSkeleton === true ||
      element?.employeeId?.startsWith('SKELETON-')
    );
  }

  /**
   * Check if there is billing history data (or if we're loading, which shows skeleton)
   */
  hasBillingHistory(): boolean {
    return this.isLoading || (this.billingData && this.billingData.length > 0);
  }

  /**
   * Get the full name of a resource
   */
  getResourceFullName(element: BillingResourceData): string {
    return `${element.firstName} ${element.lastName}`.trim();
  }

  /**
   * Check if the current user can access a resource based on practice areas
   */
  canAccessResource(element: BillingResourceData): boolean {
    if (!element.practiceAreaCodes || element.practiceAreaCodes.length === 0) {
      return true;
    }

    // Get the user's practice areas
    const userPracticeAreas = this.authService.getCurrentUserPracticeAreas();

    // If user has no practice areas, they can't access any restricted resources
    if (!userPracticeAreas || userPracticeAreas.length === 0) {
      return false;
    }

    // Check if user has at least one matching practice area
    const userPracticeAreaCodes = userPracticeAreas.map(
      (pa) => pa.practiceCode,
    );
    return element.practiceAreaCodes.some((paCode) =>
      userPracticeAreaCodes.includes(paCode),
    );
  }

  navigateToResource(employeeId: string, event: Event): void {
    event.preventDefault();
    if (!employeeId) return;
    const breadcrumbContext = this.breadcrumbContext();
    const state = breadcrumbContext.length
      ? {
          breadcrumbOverride: [
            ...breadcrumbContext,
            { label: ':employeeId', url: '' },
          ],
        }
      : undefined;
    this.router.navigate(['/resources', employeeId], { state });
  }
}
